import React, { useRef } from 'react';
import { PropTypes } from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Typography,
  Grid
} from '@mui/material';
import { FilePond, FileStatus } from 'react-filepond';
import filePondGlobalStyles from '@seccon/core/filepond/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import '@seccon/core/filepond/plugins';

import CustomFormLabel from '@seccon/shared/ui/components/CustomFormLabel';
import CSRFContext from '@seccon/core/contexts/csrf';
import decodeJson from '@seccon/shared/utils/decodeJson';

class PasswordProtectedException extends Error {
  main = 'Password protected files are not allowed'
}

const DocumentsForm = React.forwardRef(({ setIsValidating }) => {
  const { control, setError, setValue } = useFormContext();
  const csrfToken = React.useContext(CSRFContext);

  // Keeps track of the invalid files added to the list
  const invalidFiles = useRef([]);

  // Keeps track of the number of files that are currently being async validated
  const validatingFiles = useRef(0);

  return (
    <Controller
      name='foreignTravel.documents'
      control={control}
      defaultValue={[]}
      render={({ field: { onChange, value }, fieldState }) => {
        const { error } = fieldState;

        const handlePasswordCheck = (fileItems) => (
          // eslint-disable-next-line no-async-promise-executor
          new Promise(async (resolve, reject) => {
            const formData = new FormData();
            formData.append('file', fileItems);

            const response = await fetch('/foreignTravel/isFilePasswordProtected', {
              method: 'POST',
              headers: {
                'anti-csrf-token': csrfToken
              },
              body: formData
            });

            // Silently fail and resolve the promise (do not block the form if the call fails)
            if (!response.ok) {
              resolve();

              return;
            }

            const data = await decodeJson(response);

            if (data.status) {
              reject(new PasswordProtectedException());
            } else {
              resolve();
            }
          })
        );

        const handleUpdateFileList = (fileItems) => {
          const newInvalidFiles = [];
          const newFiles = [];

          for (const entry of fileItems) {
            newFiles.push(entry.file);

            // Based on the new files coming in, see if its in the invalid file list and build a new invalid file list
            if (invalidFiles.current.some((invalidFile) => invalidFile === entry.id)) {
              newInvalidFiles.push(entry.id);
            }
          }

          // Calling onChange re-runs validation rules, which there are none configured, so it would clear errors set in handleError
          // Calling setValue allows me to pass `shouldValidate: false`, which skips validation checks
          // However, I want to do validation checks and clear any errors if the user removed the last invalid file
          setValue('foreignTravel.documents', newFiles, { shouldValidate: newInvalidFiles.length === 0 });

          invalidFiles.current = newInvalidFiles;
        };

        const handleError = (err, file) => {
          setError('foreignTravel.documents', { type: 'custom', message: err.main });

          invalidFiles.current = [
            ...new Set([...invalidFiles.current, file.id])
          ];
        };

        const handleClearAll = () => {
          onChange([]);
        };

        const handleAddFileStart = (file) => {
          // If the file that was added is to be processed, set validating to true and add it to the counter
          if (file.status === FileStatus.PROCESSING_QUEUED) {
            setIsValidating(true);
            validatingFiles.current++;
          }
        }

        const handleProcessEnd = () => {
          // For each file that is finished processing, remove it from the counter
          validatingFiles.current--;

          // If all the files that were being processed are finished, set validating to false
          if (validatingFiles.current === 0) {
            setIsValidating(false);
          }
        }

        return (
          <Box sx={{ mt: 2 }}>
            {filePondGlobalStyles}
            <Typography sx={{ marginBottom: 3 }}>Please upload a copy of your travel itinerary</Typography>
            <FormControl fullWidth error={Boolean(error)} required>
              <CustomFormLabel label='Document Files (Max 10 Files)'>
                <FilePond
                  credits={false}
                  files={value}
                  fileValidateTypeDetectType={(source, type) => {
                    // Any file with a xlsx extension should be normalized to the spreadsheet mime type
                    if (/.xlsx$/.test(source.name)) {
                      return Promise.resolve('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                    }

                    return Promise.resolve(type);
                  }}
                  allowMultiple
                  maxFiles={10}
                  // We want all the uploaded files to be validated all at once
                  // Without this property, its possible that isValidating would be set back and forth for each chunk being processed
                  maxParallelUploads={10}
                  acceptedFileTypes={[
                    'application/pdf',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/vnd.ms-excel',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/custom'
                  ]}
                  maxTotalFileSize='25MB'
                  name='files'
                  labelIdle='Drag & Drop your files or <span class="filepond--label-action">Browse</span>'
                  labelFileTypeNotAllowed='Only PDF, Excel, and Word files are allowed'
                  labelMaxTotalFileSizeExceeded='Cannot upload more than 25MB of files at a time'
                  onaddfilestart={handleAddFileStart}
                  onerror={handleError}
                  onprocessfile={handleProcessEnd}
                  onupdatefiles={handleUpdateFileList}
                  server={{
                    process: (fieldName, file, metadata, load, err, progress, abort) => {
                      handlePasswordCheck(file)
                        .then(() => load())
                        .catch((validationError) => err(validationError));

                      return { abort };
                    },
                    fetch: null,
                    revert: null
                  }}
                />
              </CustomFormLabel>
              {error && (
                <FormHelperText>
                  {error.message}
                </FormHelperText>
              )}
            </FormControl>

            <Grid container justifyContent='flex-start' alignItems='center' sx={{ mt: 2 }}>
              <Grid item>
                {value.length > 0 && (
                  <Button
                    variant='text'
                    startIcon={<DeleteIcon />}
                    color='error'
                    onClick={handleClearAll}
                  >
                    Clear All
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        );
      }}
    />
  );
});

DocumentsForm.propTypes = {
  setIsValidating: PropTypes.func.isRequired
};

export default DocumentsForm;
